<template>
  <section id="home" class="hero-section">
    <img
      class="hero-img"
      :src="bannerImage"
      alt=""
      fetchpriority="high"
      decoding="async"
    />
    <div class="hero-overlay"></div>
    <div class="hero-scrim"></div>

    <div class="hero-content">
      <div v-if="showLogo" class="logo-container">
        <!-- Loading skeleton -->
        <div v-if="logoLoading" class="logo-skeleton"></div>
          <!-- Actual logo -->
        <img
          v-else-if="logoImage"
          :src="logoImage"
          alt="Logo"
          class="logo"
        />
      </div>

      <!-- Open / closed status + city -->
      <p v-if="isSettingsLoaded" class="hero-status">
        <span class="status-dot" :class="{ closed: openStatus.state !== 'open' }" aria-hidden="true"></span>
        <span class="status-text">
          <template v-if="openStatus.state === 'open'">{{ $t('hero.openUntil', { time: openStatus.until }) }}</template>
          <template v-else-if="openStatus.state === 'opensLater'">{{ $t('hero.opensAt', { time: openStatus.at }) }}</template>
          <template v-else>{{ $t('hero.closedToday') }}</template>
        </span>
        <span v-if="city" class="hero-city">· {{ city }}</span>
      </p>

      <template v-if="showHeroText">
        <h1 class="hero-title">{{ businessName }}</h1>
        <p class="hero-subtitle">{{ businessMotto }}</p>
      </template>

      <!-- CTA row — always rendered, independent of showHeroText -->
      <div class="hero-cta-row">
        <button
          id="hero-cta"
          type="button"
          class="hero-book-btn"
          @pointerdown="prefetchBookingSheet"
          @click="openBookingSheet({ source: 'hero' })"
        >
          {{ $t('services.bookNow') }}
        </button>
        <a
          v-if="voucherUrl"
          :href="voucherUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="voucher-btn"
        >
          {{ $t('hero.voucherBtn') }}
        </a>
      </div>

      <div v-if="imagesLoading" class="loading-indicator">
        <div class="spinner"></div>
      </div>
    </div>
  </section>
</template>

<script>
import { useTenantConfig } from '@/composables/useTenantConfig'
import { useBusinessSettings } from '@/composables/useBusinessSettings'
import { useBookingSheet } from '@/composables/useBookingSheet'
import { loadBannerImage, loadLogoImage } from '@/config/imageConfig'
import { ref, computed, onMounted } from 'vue'

// "Bismarck Boulevard 4, 79098 Freiburg, Germany" -> "Freiburg".
// Prefer the last comma-part that carries a postal code (never the street, which
// is the first part); otherwise fall back to the last part. Digits are stripped.
function cityFromAddress(address) {
  const parts = String(address || '')
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean)
  if (parts.length === 0) return ''
  const withDigits = parts.slice(1).filter((p) => /\d/.test(p))
  const candidate = withDigits.length ? withDigits[withDigits.length - 1] : parts[parts.length - 1]
  return candidate.replace(/\d+/g, '').replace(/\s{2,}/g, ' ').trim()
}

export default {

  name: 'HeroSection',
  setup() {
    const { config } = useTenantConfig()
    const { openStatus, businessAddress, isLoaded: isSettingsLoaded } = useBusinessSettings()
    const { open: openBookingSheet, prefetch: prefetchBookingSheet } = useBookingSheet()
    const bannerImage = ref('/images/default/banner.webp')
    const logoImage = ref(null)
    const imagesLoading = ref(true)
    const logoLoading = ref(true)

    onMounted(async () => {
      try {
        const images = config.value?.images
        bannerImage.value = await loadBannerImage(images)
        logoImage.value = await loadLogoImage(images)
      } catch (error) {
        console.error('Error loading images:', error)
        bannerImage.value = '/images/default/banner.webp'
        logoImage.value = null
      } finally {
        imagesLoading.value = false
        logoLoading.value = false
      }
    })

    const voucherUrl = computed(() => config.value?.voucherUrl || '')
    const city = computed(() => cityFromAddress(businessAddress.value))

    return {
      bannerImage,
      logoImage,
      imagesLoading,
      logoLoading,
      voucherUrl,
      openStatus,
      isSettingsLoaded,
      city,
      openBookingSheet,
      prefetchBookingSheet
    }
  },
  props: {
    businessName: {
      type: String,
      required: true,
      default: 'Traditional Thai Massage'
    },
    businessMotto: {
      type: String,
      required: true,
      default: 'Experience authentic healing and relaxation'
    },
    showHeroText: {
      type: Boolean,
      default: true
    },
    showLogo: {
      type: Boolean,
      default: true
    }
  }
}
</script>

<style scoped>
.hero-section {
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: flex-end;
  width: 100%;
  /* The photo proportion (4:5 here, 16:10 tablet, 21:9 desktop) is a FLOOR, not a
     fixed height. Do not go back to `aspect-ratio` + `min-height`: an explicit
     min-height disables aspect-ratio's content-based minimum, so the box stays
     at the ratio height and, being bottom-aligned (flex-end) with overflow
     hidden, a long business name that wraps pushes the logo up out of the top
     edge, under the sticky header. `height: auto` + min-height lets it grow.
     vw ≈ the section width (full-bleed; only a desktop scrollbar differs). */
  min-height: max(62vh, 125vw);
  min-height: max(62svh, 125vw);
  padding: var(--s-6) var(--gutter) var(--s-7);
  color: #fff;
}

/* Photo layer — replaces the former CSS background-image */
.hero-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  z-index: 0;
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg,
    var(--hero-overlay-start, rgba(255, 255, 255, 0.3)),
    var(--hero-overlay-end, rgba(255, 255, 255, 0.5))
  );
  z-index: 1;
}

/* Keeps the headline / CTA legible over any photo */
.hero-scrim {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, var(--surface-page), transparent 55%);
  pointer-events: none;
  z-index: 2;
}

/* Fade the hero into the page background — must use --page-top (a solid color),
   not --background which is a gradient string and cannot be a color stop.
   Phase 3 dropped the -120px OffersSection overlap, so this only needs to cover
   the seam now; a tall fade on top of .hero-scrim washed the hero bottom out. */
.hero-section::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 22%;
  background: linear-gradient(
    to bottom,
    transparent 0%,
    var(--page-top, #EAF3FF) 100%
  );
  pointer-events: none;
  z-index: 3;
}

.hero-content {
  position: relative;
  z-index: 4;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  text-align: left;
  animation: heroEnter 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
}

@keyframes heroEnter {
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
}

@media (prefers-reduced-motion: reduce) {
  .hero-content {
    animation: none;
  }
}

.logo {
  width: 250px;
  height: auto;
  object-fit: contain;
  margin-bottom: 1rem;
}

.logo-container {
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.logo-skeleton {
  width: 250px;
  height: 250px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  animation: pulse 1.5s infinite;
}

/* Open status pill */
.hero-status {
  display: inline-flex;
  align-items: center;
  gap: var(--s-2);
  padding: 6px 12px;
  border-radius: 999px;
  background: var(--surface-card);
  border: 1px solid var(--border-light);
  color: var(--text-primary);
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: var(--s-3);
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--color-success);
  flex-shrink: 0;
}

.status-dot.closed {
  background: var(--color-closed);
}

.hero-city {
  color: var(--text-muted);
  font-weight: 500;
}

.hero-title {
  font-size: 3.5rem;
  margin-bottom: 1rem;
  text-shadow: 2px 2px 4px var(--text-shadow-color);
  font-weight: bold;
  color: var(--color-heading);
}

.hero-subtitle {
  font-size: 1.8rem;
  opacity: 0.95;
  text-shadow: 1px 1px 3px var(--text-shadow-color);
  color: var(--text-subtitle);
  margin-bottom: 1.5rem;
}

/* ── CTA row ── */
.hero-cta-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--s-3);
  margin-top: var(--s-4);
}

.hero-book-btn,
.voucher-btn {
  min-height: 52px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 var(--s-6);
  border-radius: 50px;
  font-size: 1.05rem;
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.hero-book-btn {
  border: none;
  /* Solid CTA colour, not --gradient-brand: the gradient starts from
     brand.overlay, which is semi-transparent on many tenants (the demo tenant's
     is #0D596E80), so over a photo the primary CTA washes out. --cta-bg is
     brand.primary unless cta.bg is set; --cta-fg is contrast-picked against it. */
  background: var(--cta-bg);
  color: var(--cta-fg);
  box-shadow: var(--shadow-brand);
  font-family: inherit;
  position: relative;
  overflow: hidden;
  /* Glow heartbeat — moved here from the voucher button (primary CTA now) */
  animation: ctaPulse 3s ease-in-out infinite;
}

.hero-book-btn::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    110deg,
    rgba(255, 255, 255, 0)    30%,
    rgba(255, 255, 255, 0.38) 50%,
    rgba(255, 255, 255, 0)    70%
  );
  transform: translateX(-100%);
  animation: ctaShimmer 3s ease-in-out infinite;
  border-radius: inherit;
  pointer-events: none;
}

.hero-book-btn:hover {
  animation: none;
  transform: translateY(-2px);
}

.hero-book-btn:active {
  animation: none;
  transform: translateY(0) scale(0.97);
}

.hero-book-btn:focus-visible,
.voucher-btn:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring);
}

/* Secondary action */
.voucher-btn {
  background: var(--surface-card);
  color: var(--text-primary);
  border: 1.5px solid var(--border-main);
  font-weight: 600;
}

.voucher-btn:hover {
  background: var(--surface-hover);
}

.voucher-btn:active {
  transform: scale(0.98);
}

@keyframes ctaPulse {
  0%, 70%, 100% {
    transform: translateY(0) scale(1);
  }
  80% {
    transform: translateY(-3px) scale(1.03);
  }
  90% {
    transform: translateY(0) scale(1);
  }
}

@keyframes ctaShimmer {
  0%        { transform: translateX(-100%); }
  55%, 100% { transform: translateX(200%); }
}

/* No pulse / shimmer for users who ask for less motion */
@media (prefers-reduced-motion: reduce) {
  .hero-book-btn,
  .hero-book-btn::before {
    animation: none;
  }

  .hero-book-btn:hover,
  .hero-book-btn:active,
  .voucher-btn:active {
    transform: none;
  }
}

/* On touch: keep animation running, disable hover override */
@media (hover: none) {
  .hero-book-btn:hover {
    animation: ctaPulse 3s ease-in-out infinite;
    transform: none;
  }

  .voucher-btn:hover {
    background: var(--surface-card);
  }
}

.loading-indicator {
  margin-top: 1rem;
  display: flex;
  justify-content: flex-start;
  align-items: center;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid var(--border-light);
  border-top: 2px solid var(--color-accent);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes pulse {
  0%, 100% { opacity: 0.3; }
  50% { opacity: 0.6; }
}

/* Full-width CTAs on the narrowest phones */
@media (max-width: 479px) {
  .hero-cta-row {
    flex-direction: column;
    align-items: stretch;
  }

  .hero-book-btn,
  .voucher-btn {
    width: 100%;
  }
}

@media (max-width: 768px) {
  .logo {
    width: 170px;
  }

  .logo-skeleton {
    width: 120px;
    height: 120px;
  }

  .hero-title {
    font-size: 2.2rem;
  }

  .hero-subtitle {
    font-size: 1.2rem;
  }
}

/* Tablet */
@media (min-width: 769px) and (max-width: 1023px) {
  .logo {
    width: 200px;
  }

  .hero-section {
    min-height: max(62vh, 62.5vw);
    min-height: max(62svh, 62.5vw);
  }
}

/* Desktop polish layer */
@media (min-width: 1024px) {
  .hero-section {
    min-height: max(520px, calc(100vw * 9 / 21));
    padding: var(--s-7) var(--gutter) var(--s-8);
  }

  .hero-title {
    font-size: 4.5rem;
    letter-spacing: 0.02em;
  }

  .hero-subtitle {
    font-size: 2.1rem;
  }
}
</style>
